import * as Sentry from "@sentry/react";

import { CustomRequestInit } from "@/api/rest/core/types";

import { getPlatform } from "./platform";

export const FILTERED_ERROR_MESSAGES = [
  "Authentication failed after token refresh attempt",
  "Token refresh succeeded but no new token found",
  "User not found, or password is incorrect",
];

const FILTERED_BROWSER_NETWORK_MESSAGES = ["Load failed", "Failed to fetch"];

const FILTERED_SENTRY_UNHANDLED_REJECTION_MESSAGES = [
  "Object captured as promise rejection with keys: [object has no keys]",
  "Event `CustomEvent` (type=unhandledrejection) captured as promise rejection",
];

const FILTERED_BROWSER_EXTENSION_PATTERNS = [/Object Not Found Matching Id/i];

type ErrorLike = {
  message?: unknown;
  name?: unknown;
  reason?: unknown;
  type?: unknown;
  value?: unknown;
};

const normalizeErrorForFiltering = (error: unknown): { name: string; message: string } => {
  const messages: string[] = [];
  let name = "";

  const addMessage = (value: unknown) => {
    if (typeof value === "string" && value.trim()) {
      messages.push(value);
    }
  };

  if (error instanceof Error) {
    name = error.name;
    addMessage(error.message);
  } else if (typeof error === "string") {
    addMessage(error);
  } else if (error && typeof error === "object") {
    const errorLike = error as ErrorLike;

    if (typeof errorLike.name === "string") {
      name = errorLike.name;
    } else if (typeof errorLike.type === "string") {
      name = errorLike.type;
    }

    addMessage(errorLike.message);
    addMessage(errorLike.value);
    addMessage(errorLike.reason);
  } else if (error !== null && error !== undefined) {
    addMessage(String(error));
  }

  return {
    name,
    message: messages.join(" "),
  };
};

export const shouldFilterError = (error: unknown): boolean => {
  const { name, message } = normalizeErrorForFiltering(error);

  if (name === "AbortError" || message === "AbortError" || message.startsWith("AbortError:")) {
    return true;
  }

  if (FILTERED_ERROR_MESSAGES.some((filteredMessage) => message.includes(filteredMessage))) {
    return true;
  }

  if (FILTERED_BROWSER_NETWORK_MESSAGES.includes(message)) {
    return true;
  }

  if (FILTERED_SENTRY_UNHANDLED_REJECTION_MESSAGES.includes(message)) {
    return true;
  }

  if (
    message.includes("Failed after") &&
    message.includes("(network error)") &&
    message.includes("Last error: Failed to fetch")
  ) {
    return true;
  }

  return FILTERED_BROWSER_EXTENSION_PATTERNS.some((pattern) => pattern.test(message));
};

// Common metadata collector for both spans and errors
interface RequestMetadata {
  url?: string;
  method?: string;
  requestBody?: any;
  responseBody?: any;
  responseStatus?: number;
  responseHeaders?: Record<string, string>;
  timestamp?: string;
  environment?: string;
  platform?: string;
}

// Collect complete request metadata
const collectRequestMetadata = (
  url?: string,
  method?: string,
  options?: CustomRequestInit,
  response?: Response,
  responseBody?: any,
): RequestMetadata => {
  const metadata: RequestMetadata = {
    url,
    method,
    timestamp: new Date().toISOString(),
    environment: import.meta.env.MODE,
    platform: getPlatform(),
  };

  // Add request data if available
  if (options?.body) {
    metadata.requestBody = options.body instanceof FormData ? "FormData" : options.body;
  }

  // Add response data if available
  if (response) {
    metadata.responseStatus = response.status;
    metadata.responseBody = responseBody;

    // Collect relevant headers
    const relevantHeaders = ["content-type", "content-length", "x-request-id", "cf-ray"];

    metadata.responseHeaders = relevantHeaders.reduce(
      (acc, header) => {
        const value = response.headers.get(header);
        if (value) {
          acc[header] = value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  return metadata;
};

export const createSpanAttributes = (url: string, method: string) => ({
  "http.url": new URL(url).pathname,
  "http.method": method,
  environment: import.meta.env.MODE,
});

export const updateSpanWithResponse = (span: Sentry.Span, response: Response) => {
  span.setAttributes({
    "http.status_code": response.status,
  });

  if (!response.ok) {
    span.setStatus(Sentry.getSpanStatusFromHttpCode(response.status));
  }
};

export const sendToSentry = (
  error: Error,
  context: {
    url?: string;
    method?: string;
    options?: CustomRequestInit;
    response?: Response;
    responseBody?: any;
    additionalContext?: Record<string, any>;
  },
) => {
  if (shouldFilterError(error)) {
    // Optionally log to console in development
    if (import.meta.env.MODE === "development") {
      console.debug("Filtered Sentry error:", error.message);
    }
    return; // Exit early without sending to Sentry
  }

  const { url, method, options, response, responseBody, additionalContext } = context;

  Sentry.withScope((scope) => {
    // Collect complete request metadata
    const metadata = collectRequestMetadata(url, method, options, response, responseBody);

    // Set error fingerprint for better grouping
    scope.setFingerprint([
      "api-error",
      metadata.url || "unknown-url",
      metadata.method || "unknown-method",
      String(metadata.responseStatus || "unknown-status"),
    ]);

    // Add rich context to error
    scope.setContext("request", {
      url: metadata.url,
      method: metadata.method,
      headers: options?.headers,
      body: metadata.requestBody,
    });

    if (response) {
      scope.setContext("response", {
        status: metadata.responseStatus,
        headers: metadata.responseHeaders,
        body: metadata.responseBody,
      });
    }

    scope.setContext("environment", {
      mode: metadata.environment,
      platform: metadata.platform,
      timestamp: metadata.timestamp,
    });

    // Add any additional context
    if (additionalContext) {
      scope.setContext("additional", additionalContext);
    }

    // Set error level based on response status
    if (response?.status) {
      scope.setLevel(response.status >= 500 ? "error" : "warning");
    }

    Sentry.captureException(error);
  });
};
